/**
 * TabErrorBoundary — wraps each CRM tab so a render error in one tab
 * doesn't take down the whole CRM page. The user can navigate away to
 * a working tab and the bad one shows a graceful fallback message.
 *
 * Why this exists
 *   Before the grouped-nav consolidation, several tabs (Memberships,
 *   Reputation, Funnels & Forms, Custom Objects) had latent bugs that
 *   nobody hit because the 49-tab flat scroll buried them. The new
 *   dropdown nav surfaced them and any one crash would error-out the
 *   entire CRMPage. This boundary contains the blast radius.
 *
 * Design
 *   • Per-tab boundary (one instance per route) — when the user
 *     switches tabs, React unmounts the boundary and re-mounts a fresh
 *     one for the new tab, automatically clearing any prior error state.
 *   • Logs the full error + component stack to console for debugging.
 *   • The fallback UI matches the existing CRM tab aesthetic.
 */
"use strict";

import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the console log loud so anything that gets caught is still
    // diagnosable in production via DevTools.
    // eslint-disable-next-line no-console
    console.error(
      `[CRM][TabErrorBoundary] tab "${this.props.tabName || "unknown"}" crashed:`,
      error,
      errorInfo
    );
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          background: "#FFF7E6",
          border: "1px solid #F5C26B",
          borderRadius: 12,
          padding: 20,
          margin: "12px 4px",
          maxWidth: 720,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <AlertTriangle size={20} color="#B8740F" />
          <h3 style={{ margin: 0, fontSize: 15, color: "#7A4A00", fontWeight: 600 }}>
            This tab is temporarily unavailable
          </h3>
        </div>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#5C3A00", lineHeight: 1.5 }}>
          The "{this.props.tabName || "tab"}" view hit a render error and was
          isolated to keep the rest of the CRM working. Switch to another
          tab via the navigation above, or try again below. The engineering
          team has been notified through console error logging.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={this.handleRetry}
            type="button"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              border: "none",
              background: "#F5C26B",
              color: "#5C3A00",
              fontWeight: 600,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            <RefreshCw size={12} /> Try again
          </button>
        </div>
        {this.state.error && (
          <details style={{ marginTop: 12, fontSize: 11, color: "#7A4A00" }}>
            <summary style={{ cursor: "pointer" }}>Error detail (for support)</summary>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                background: "rgba(245, 194, 107, 0.18)",
                padding: 8,
                borderRadius: 6,
                marginTop: 6,
              }}
            >
              {String(this.state.error?.message || this.state.error)}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

export default TabErrorBoundary;
